import { trackProductEvent } from "./productAnalytics";
import { readRestaurantInviteSession } from "./restaurantInviteAccess";

const INSTALLED_FLAG = "__venaqqiRelatedPlaceNavigationInstalled";

const nextFrame = callback => {
  if (typeof window === "undefined") return;
  const raf = window.requestAnimationFrame || (fn => window.setTimeout(fn, 0));
  raf(() => raf(callback));
};

export const placeSlugFromName = value => String(value || "")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/(^-|-$)/g, "");

export const syncRelatedPlaceUrl = placeName => {
  if (typeof window === "undefined" || !placeName) return false;
  const slug = placeSlugFromName(placeName);
  if (!slug) return false;

  const params = new URLSearchParams(window.location.search || "");
  const inviteSession = readRestaurantInviteSession();

  // Preserve/recover the restaurant invitation context so a hard refresh keeps
  // the same guest bridge and the exact place currently being viewed.
  if (inviteSession?.inviteId && !params.get("invite")) params.set("invite", inviteSession.inviteId);
  if (inviteSession?.source && !params.get("source")) params.set("source", inviteSession.source);
  params.set("lugar", slug);

  const nextUrl = `${window.location.pathname || "/"}?${params.toString()}${window.location.hash || ""}`;
  window.history.replaceState(window.history.state, "", nextUrl);
  return true;
};

const findLatestDetailScrollContainer = () => {
  if (typeof document === "undefined") return null;
  const relatedSections = Array.from(document.querySelectorAll('[data-testid="related-places"]'));
  const latestSection = relatedSections[relatedSections.length - 1];
  if (!latestSection) return null;

  let node = latestSection.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle?.(node);
    if (style?.overflowY === "auto" || style?.overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

export const scrollLatestRelatedDetailToTop = () => {
  const container = findLatestDetailScrollContainer();
  if (!container) return false;
  if (typeof container.scrollTo === "function") container.scrollTo({ top: 0, left: 0, behavior: "auto" });
  else container.scrollTop = 0;
  return true;
};

export const installRelatedPlaceNavigationEnhancements = () => {
  if (typeof window === "undefined" || typeof document === "undefined" || window[INSTALLED_FLAG]) return;
  window[INSTALLED_FLAG] = true;

  document.addEventListener("click", event => {
    const button = event.target?.closest?.('[data-testid="related-places"] button[aria-label^="Abrir "]');
    if (!button) return;

    const ariaLabel = String(button.getAttribute("aria-label") || "").trim();
    const placeName = ariaLabel.replace(/^Abrir\s+/i, "").trim();
    if (placeName) {
      syncRelatedPlaceUrl(placeName);
      void trackProductEvent("place_opened", {
        place_name: placeName,
        source: "related",
        navigation_surface: "related_places",
      });
    }

    // React swaps/updates the related Detail after the click. Waiting two frames
    // lets both web and installed PWA paint the new place before resetting scroll.
    nextFrame(() => { scrollLatestRelatedDetailToTop(); });
  }, true);
};
